import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import {
  Users, BookOpen, ClipboardList, TrendingUp, Clock, CheckCircle, UserPlus
} from 'lucide-react';
import {
  PieChart, Pie, Cell, Legend, Tooltip, ResponsiveContainer
} from 'recharts';

const COLORS = ['#6366f1', '#22c55e', '#f59e0b'];

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user, isSuperAdmin } = useAuth();

  useEffect(() => { fetchStats(); }, []);

  const fetchStats = async () => {
    try {
      const { data } = await api.get('/admin/dashboard');
      setStats(data.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="loader" />
    </div>
  );

  const pieData = stats?.questionsBySubject?.map(s => ({
    name: s._id, value: s.count
  })) || [];

  const superadmin = isSuperAdmin();

  return (
    <div className="space-y-6 animate-fade-in">

      {/* Page header row — Create Admin button for superadmin in top-right */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-400 text-sm">Overview of your EAMCET portal</p>
        </div>
        {superadmin && (
          <Link
            to="/admin/create-admin"
            className="btn-primary flex items-center gap-2 text-sm"
          >
            <UserPlus size={15} /> Create Admin
          </Link>
        )}
      </div>
      <div className="bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-2xl px-7 py-6">
        <div className="flex items-center gap-2 mb-1">
          <CheckCircle size={18} className="text-black/70" />
          <span className="text-sm font-semibold text-black/70">
            {superadmin ? 'Super Admin' : 'Admin'}
          </span>
        </div>
        <h1 className="text-2xl font-bold text-black">Welcome back, {user?.name}!</h1>
        <p className="text-black/70 text-sm mt-1">
          Manage your EAMCET portal from the dashboard below.
        </p>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <div className="card flex items-center gap-4">
          <div className="bg-blue-50 rounded-xl p-3 flex-shrink-0">
            <Users className="text-blue-600 w-6 h-6" />
          </div>
          <div>
            <p className="text-gray-500 text-sm">Total Students</p>
            <p className="text-2xl font-bold text-gray-900">{stats?.totalStudents || 0}</p>
          </div>
        </div>
        <div className="card flex items-center gap-4">
          <div className="bg-emerald-50 rounded-xl p-3 flex-shrink-0">
            <BookOpen className="text-emerald-600 w-6 h-6" />
          </div>
          <div>
            <p className="text-gray-500 text-sm">Total Questions</p>
            <p className="text-2xl font-bold text-gray-900">{stats?.totalQuestions || 0}</p>
            {stats?.pendingApproval > 0 && (
              <p className="text-xs text-amber-600 font-medium mt-0.5">
                {stats.pendingApproval} pending review
              </p>
            )}
          </div>
        </div>
        <div className="card flex items-center gap-4">
          <div className="bg-amber-50 rounded-xl p-3 flex-shrink-0">
            <ClipboardList className="text-amber-600 w-6 h-6" />
          </div>
          <div>
            <p className="text-gray-500 text-sm">Active Tests</p>
            <p className="text-2xl font-bold text-gray-900">{stats?.totalTests || 0}</p>
          </div>
        </div>
        <div className="card flex items-center gap-4">
          <div className="bg-violet-50 rounded-xl p-3 flex-shrink-0">
            <CheckCircle className="text-violet-600 w-6 h-6" />
          </div>
          <div>
            <p className="text-gray-500 text-sm">Submissions</p>
            <p className="text-2xl font-bold text-gray-900">{stats?.totalAttempts || 0}</p>
          </div>
        </div>
      </div>

      {/* Charts & Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Pie Chart */}
        <div className="card">
          <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
            <TrendingUp size={18} className="text-yellow-500" />
            Questions by Subject
          </h3>
          {pieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={pieData} cx="50%" cy="50%" outerRadius={80}
                  dataKey="value"
                  label={({ name, value }) => `${name}: ${value}`}
                >
                  {pieData.map((_, index) => (
                    <Cell key={index} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Legend />
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-48 text-gray-400">
              <div className="text-center">
                <BookOpen size={48} className="mx-auto mb-2 opacity-30" />
                <p className="text-sm">No questions yet.{' '}
                  <Link to="/admin/questions" className="text-yellow-600 font-medium">Add questions</Link>
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="card">
          <h3 className="font-bold text-gray-800 mb-4">Quick Actions</h3>
          <div className="space-y-3">
            <Link to="/admin/students" className="flex items-center gap-3 p-3 bg-blue-50 hover:bg-blue-100 rounded-xl transition-colors">
              <Users className="text-blue-600" size={20} />
              <div className="flex-1">
                <p className="font-semibold text-gray-800 text-sm">Manage Students</p>
                <p className="text-xs text-gray-500">Add, edit, or bulk upload students</p>
              </div>
              <span className="text-blue-600 text-xs font-medium">→</span>
            </Link>
            <Link to="/admin/questions" className="flex items-center gap-3 p-3 bg-emerald-50 hover:bg-emerald-100 rounded-xl transition-colors">
              <BookOpen className="text-emerald-600" size={20} />
              <div className="flex-1">
                <p className="font-semibold text-gray-800 text-sm">Question Bank</p>
                <p className="text-xs text-gray-500">Upload or create questions</p>
              </div>
              <span className="text-emerald-600 text-xs font-medium">→</span>
            </Link>
            <Link to="/admin/tests" className="flex items-center gap-3 p-3 bg-amber-50 hover:bg-amber-100 rounded-xl transition-colors">
              <ClipboardList className="text-amber-600" size={20} />
              <div className="flex-1">
                <p className="font-semibold text-gray-800 text-sm">Create Mock Test</p>
                <p className="text-xs text-gray-500">Set up new examination</p>
              </div>
              <span className="text-amber-600 text-xs font-medium">→</span>
            </Link>
            {superadmin && (
              <Link to="/admin/create-admin" className="flex items-center gap-3 p-3 bg-yellow-50 hover:bg-yellow-100 rounded-xl transition-colors border border-yellow-200">
                <Users className="text-yellow-700" size={20} />
                <div className="flex-1">
                  <p className="font-semibold text-gray-800 text-sm">Create Admin User</p>
                  <p className="text-xs text-gray-500">Add a new admin account</p>
                </div>
                <span className="text-yellow-700 text-xs font-medium">→</span>
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Recent Submissions */}
      {stats?.recentAttempts?.length > 0 && (
        <div className="card">
          <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
            <Clock size={18} className="text-yellow-500" />
            Recent Submissions
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  {['Student', 'Test', 'Score', 'Accuracy', 'Rank'].map(h => (
                    <th key={h} className="text-left py-2 px-3 text-gray-500 font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {stats.recentAttempts.map((r, i) => (
                  <tr key={i} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                    <td className="py-3 px-3">
                      <p className="font-medium text-gray-800">{r.student?.name}</p>
                      <p className="text-xs text-gray-500">{r.student?.hallTicketNumber}</p>
                    </td>
                    <td className="py-3 px-3 text-gray-600">{r.test?.title}</td>
                    <td className="py-3 px-3">
                      <span className="font-bold text-yellow-700">{r.totalScore}</span>
                      <span className="text-gray-400 text-xs">/{r.maxScore}</span>
                    </td>
                    <td className="py-3 px-3">
                      <span className={`font-medium ${r.accuracy >= 70 ? 'text-green-600' : r.accuracy >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                        {r.accuracy}%
                      </span>
                    </td>
                    <td className="py-3 px-3">
                      <span className="bg-yellow-100 text-yellow-800 text-xs font-semibold px-2 py-1 rounded-full">
                        #{r.rank || '-'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
